/**
 * `useClickOutside` hook for capturing clicking outside of an element
 */
import type { RefObject } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { coerceArray } from '../../utils/UNSAFE_arrayUtils';
import { LOGICAL_PARENT } from '../../UNSAFE_Layer/Layer';
import { MODAL_OVERLAY, BackdropElement } from '#UNSAFE_Modal/hooks/useModal';

type Props = {
  /**
   * If true outside click is disabled
   */
  isDisabled?: boolean;
  /**
   * Reference to element or array of elements relative to which outside click is performed
   */
  ref: RefObject<HTMLElement> | RefObject<HTMLElement>[];
  /**
   * Outside click callback
   */
  handler?: (event: MouseEvent) => void;
};

/**
 * Checks if the mouse event target is inside of any of the specified containers, or if it
 * occurs in a layer whose 'logicalParent' belongs to one of the containers.
 *
 * @param event a mouse event associated with the potential "outside click" action
 * @param ref an array of references to elements that are considered "inside"
 * @returns 'true' if the event occurs "outside" of the specified list of containers
 */
const isPointOutside = (event: Event, ref: RefObject<HTMLElement>[]) => {
  const target = event.target as HTMLElement;

  // if the event has no target, consider it "inside"
  if (!target) {
    return false;
  }

  const doc = getDocument(target);
  if (!doc.contains(target)) {
    return false;
  }

  // if the target is contained in any of the target containers, it is not "outside"
  if (ref.some((elRef: RefObject<HTMLElement>) => elRef.current?.contains(target))) {
    return false;
  }

  const findLogicalParent = (elem: Element): Element | null => {
    let elemParent = elem.parentElement as any;
    while (elemParent) {
      if (elemParent[LOGICAL_PARENT]) {
        return elemParent[LOGICAL_PARENT];
      }
      elemParent = elemParent.parentElement;
    }
    return null;
  };

  const parentLayerContainsTarget = (element: HTMLElement | null, target: Node): boolean => {
    const parentLayer = element?.closest('[data-oj-layer]');
    if (parentLayer && parentLayer.contains(target)) {
      return true;
    }
    return false;
  };

  // Check if the target is logically owned by any of the refs
  let logicalParent = findLogicalParent(target);
  while (logicalParent) {
    if (ref.some((elRef: RefObject<HTMLElement>) => elRef.current?.contains(logicalParent))) {
      return false;
    }
    logicalParent = findLogicalParent(logicalParent);
  }

  // if the click target is a modal overlay and it is part of target's modal layer
  // then it's an outside click. Otherwise, treat the click as an "inside" click because
  // it is a click on another modal overlay which must be above our elem
  if ((target as BackdropElement)[MODAL_OVERLAY]) {
    if (
      !ref.some((elRef: RefObject<HTMLElement>) => {
        const modalParent = elRef.current?.closest('[data-oj-layer="modal"]');
        return modalParent && modalParent.contains(target);
      })
    ) {
      return false;
    } else {
      return true;
    }
  }

  // if the target is a child of a modal layer other than any of the refs,
  // the click occurs in another modal layer which must be above our elem
  const modalParent = target.closest('[data-oj-layer="modal"]');
  if (
    modalParent &&
    !ref.some((elRef: RefObject<HTMLElement>) => modalParent.contains(elRef.current))
  ) {
    return false;
  }

  // lastly, look for target's parent based purely on the layer hierarchy
  if (
    ref.some((elRef: RefObject<HTMLElement>) => parentLayerContainsTarget(elRef.current, target))
  ) {
    return false;
  }

  return true;
};

const getDocument = (node?: Node): Document => node?.ownerDocument ?? document;

export const useOutsideClick = ({ isDisabled: disabled = false, ref, handler }: Props) => {
  // use ref to store mutable data and not cause a re-render
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  useEffect(() => {
    const refs = coerceArray(ref);
    if (
      !handler ||
      disabled ||
      (refs.length > 0 && refs.findIndex((elRef) => elRef.current === null) > -1)
    )
      return;
    const onMouseDown = (event: MouseEvent) => {
      mouseDownTargetRef.current = event.target;
    };

    const onMouseUp = (event: MouseEvent) => {
      if (mouseDownTargetRef.current === event.target && isPointOutside(event, refs)) {
        handler?.(event);
      }
      mouseDownTargetRef.current = null;
    };
    const firstElRef = refs[0];
    const doc = getDocument(firstElRef.current!);
    if (doc) {
      doc.addEventListener('mousedown', onMouseDown, true);
      doc.addEventListener('mouseup', onMouseUp, true);

      return () => {
        doc.removeEventListener('mousedown', onMouseDown, true);
        doc.removeEventListener('mouseup', onMouseUp, true);
      };
    }
    return undefined;
  }, [disabled, handler, ref]);
};
